import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { 
  BookOpen, Sparkles, Save, Download, Upload, FileText, 
  Trash2, Clock, ChevronLeft, ChevronRight, ChevronUp, ChevronDown,
  Copy, Check, Type, AlignLeft, Lock, PenTool,
  Moon, Sun, Search, Flame, Star, Crown, X, Gift,
  BarChart3, Zap, Eye, Target, Award, TrendingUp, Lightbulb,
  Pen, Heart, Coffee
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { useAuth } from '../components/Auth/AuthProvider';
import { UpgradePrompt, useFeatureAccess } from '../components/UpgradePrompt';

const FREE_WORD_LIMIT = 50000;
const STORAGE_KEY = 'shweebum_scratchpad';
const DAILY_USAGE_KEY = 'shweebum_scratchpad_daily';
const ACHIEVEMENTS_KEY = 'shweebum_achievements';

// ── Constants ──
const DAILY_MISSIONS = [
  "Write 100 words. That's all.",
  "Write for 5 minutes. No pressure.",
  "Start messy. You can clean it later.",
  "One sentence can change everything.",
  "Don't aim for perfect. Aim for moving.",
  "Skip the beginning. Write the scene that excites you.",
  "Write dialogue only. No descriptions. Just voices.",
  "Pick a memory. Turn it into a scene.",
  "What's your character afraid of? Write that moment.",
  "Write something that surprises you.",
];

const STARTER_PROMPTS = [
  "You would never believe what happened the night I decided to leave.",
  "Yesterday, I found myself standing in a place I swore I'd never return to.",
  "The last thing she said to me before she disappeared was, \"Don't follow me.\"",
  "It was the kind of morning that made you forget the world had ever been cruel.",
  "They told me my mother had been dead for ten years. But I just saw her yesterday.",
  "The letter arrived on a Tuesday, and by Wednesday, everything I knew was a lie.",
  "I wasn't supposed to survive. That's what they kept telling me.",
];

const DAILY_HINTS = [
  "Strong verbs beat adverbs.",
  "Specific details make scenes feel real.",
  "Dialogue lands better with contrast.",
  "Short sentences increase tension.",
  'Cut "that" — your sentence probably doesn\'t need it.',
  "Start scenes in the middle of action.",
  "Every character wants something in every scene.",
  "Vary sentence length to control rhythm.",
  "Show emotion through action, not adjectives.",
  "End chapters with a question in the reader's mind.",
];

const MILESTONE_THRESHOLDS = [50, 100, 250, 500];
const DOPAMINE_MESSAGES = {
  50: "Nice start.",
  100: "You're rolling.",
  250: "Momentum building.",
  500: "Strong session.",
};

const ACHIEVEMENT_DEFS = [
  { id: 'first_100', label: 'First 100 Words', icon: Pen, check: (ctx) => ctx.totalWords >= 100 },
  { id: 'first_10min', label: '10-Minute Session', icon: Clock, check: (ctx) => ctx.sessionSeconds >= 600 },
  { id: 'streak_3', label: '3-Day Streak', icon: Flame, check: (ctx) => ctx.streak >= 3 },
  { id: 'day_500', label: '500 Words in a Day', icon: TrendingUp, check: (ctx) => ctx.dailyWords >= 500 },
];

// ── Helpers ──
const fleschKincaid = (text) => {
  const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 3);
  const words = text.split(/\s+/).filter(w => w.length > 0);
  const syllableCount = words.reduce((acc, w) => {
    const count = w.toLowerCase().replace(/[^a-z]/g, '').replace(/[^aeiouy]+/g, ' ').trim().split(' ').filter(Boolean).length || 1;
    return acc + count;
  }, 0);
  if (!sentences.length || !words.length) return null;
  const score = 0.39 * (words.length / sentences.length) + 11.8 * (syllableCount / words.length) - 15.59;
  return Math.max(1, Math.round(score));
};

const fmtTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
const getDayIndex = () => {
  const d = new Date();
  return Math.floor(d.getTime() / 86400000);
};

const IGNORE_WORDS = new Set(['the','a','an','and','or','but','in','on','at','to','for','of','with','is','was','are','were','he','she','they','it','i','we','you','this','that','have','had','has','will','be','been','do','did','my','his','her','their','our','said','then','when','from','up','out','not','all','as','if','so','by']);
const WEAK_WORDS = ['very','really','just','quite','rather','maybe','perhaps','basically','literally','obviously','clearly','simply','actually','certainly','probably','got','get','thing','stuff','nice','good','bad','big','small','make','like'];
const PASSIVE_REGEX = /\b(was|were|is|are|been|being|be)\s+\w+ed\b/gi;

// ── Component ──
const FreeScratchpad = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const autoSaveTimer = useRef(null);
  const typingPauseTimer = useRef(null);
  const wowVisibleRef = useRef(false);
  const sessionStart = useRef(Date.now());
  const lastTypingRef = useRef(Date.now());
  const flowIntervalRef = useRef(null);

  // Core state
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('Untitled');
  const [startedWriting, setStartedWriting] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [lastSaved, setLastSaved] = useState(null);
  const [dailyWordsUsed, setDailyWordsUsed] = useState(0);
  const [copied, setCopied] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('export');

  // Mode state
  const [writingMode, setWritingMode] = useState('freewrite'); // freewrite | goal | focus
  const [kindleMode, setKindleMode] = useState(false);
  const focusMode = writingMode === 'focus';

  // Sidebar
  const [rightTab, setRightTab] = useState('progress'); // progress | improve | enhance

  // Analysis
  const [wowMessage, setWowMessage] = useState(null);
  const [overusedWords, setOverusedWords] = useState([]);
  const [passiveCount, setPassiveCount] = useState(0);
  const [weakWordMatches, setWeakWordMatches] = useState([]);

  // Tracking
  const [sessionSeconds, setSessionSeconds] = useState(0);
  const [streak, setStreak] = useState(0);

  // Search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMatches, setSearchMatches] = useState([]);
  const [searchIdx, setSearchIdx] = useState(0);

  // New features
  const [sessionMilestones, setSessionMilestones] = useState(new Set());
  const [shownToasts, setShownToasts] = useState(new Set());
  const [achievements, setAchievements] = useState({});
  const [flowStatus, setFlowStatus] = useState(null); // warming | flow | locked
  const [sessionReflection, setSessionReflection] = useState(null);
  const [continuousWritingStart, setContinuousWritingStart] = useState(null);

  const userTier = user?.subscription || 'free';
  const { canExport, canImport } = useFeatureAccess(userTier);
  const isFocus = writingMode === 'focus';
  const isGoal = writingMode === 'goal';
  const isKindle = kindleMode && isFocus;

  // ── Initialization ──
  useEffect(() => {
    window.scrollTo(0, 0);
    // Load saved content
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const data = JSON.parse(saved);
        setContent(data.content || '');
        setTitle(data.title || 'Untitled');
        setLastSaved(data.lastSaved ? new Date(data.lastSaved) : null);
      } catch (e) { /* ignore */ }
    }
    // Daily usage
    const today = new Date().toDateString();
    const usageData = localStorage.getItem(DAILY_USAGE_KEY);
    if (usageData) {
      try {
        const data = JSON.parse(usageData);
        if (data.date === today) setDailyWordsUsed(data.words || 0);
        else localStorage.setItem(DAILY_USAGE_KEY, JSON.stringify({ date: today, words: 0 }));
      } catch { localStorage.setItem(DAILY_USAGE_KEY, JSON.stringify({ date: today, words: 0 })); }
    }
    // Streak
    const lastDate = localStorage.getItem('shweebum_last_write_date');
    const currentStreak = parseInt(localStorage.getItem('shweebum_write_streak') || '0');
    if (lastDate === today) {
      setStreak(currentStreak);
    } else if (lastDate && new Date(today) - new Date(lastDate) <= 86400000) {
      const s = currentStreak + 1;
      localStorage.setItem('shweebum_write_streak', String(s));
      localStorage.setItem('shweebum_last_write_date', today);
      setStreak(s);
    } else {
      localStorage.setItem('shweebum_write_streak', '1');
      localStorage.setItem('shweebum_last_write_date', today);
      setStreak(1);
    }
    // Load achievements
    try {
      const savedAch = JSON.parse(localStorage.getItem(ACHIEVEMENTS_KEY) || '{}');
      setAchievements(savedAch);
    } catch { /* ignore */ }
    // Session timer
    const timer = setInterval(() => setSessionSeconds(s => s + 1), 1000);
    return () => {
      clearInterval(timer);
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
      if (typingPauseTimer.current) clearTimeout(typingPauseTimer.current);
      if (flowIntervalRef.current) clearInterval(flowIntervalRef.current);
    };
  }, []);

  // ── Flow Detection ──
  useEffect(() => {
    flowIntervalRef.current = setInterval(() => {
      if (!continuousWritingStart) { setFlowStatus(null); return; }
      const elapsed = (Date.now() - continuousWritingStart) / 1000;
      const sinceLastType = (Date.now() - lastTypingRef.current) / 1000;
      if (sinceLastType > 15) {
        setContinuousWritingStart(null);
        setFlowStatus(null);
        return;
      }
      if (elapsed >= 600) setFlowStatus('locked');
      else if (elapsed >= 300) setFlowStatus('flow');
      else if (elapsed >= 120) setFlowStatus('warming');
    }, 5000);
    return () => { if (flowIntervalRef.current) clearInterval(flowIntervalRef.current); };
  }, [continuousWritingStart]);

  // ── Achievement Checker ──
  const checkAchievements = useCallback((ctx) => {
    const updated = { ...achievements };
    let changed = false;
    ACHIEVEMENT_DEFS.forEach(def => {
      if (!updated[def.id] && def.check(ctx)) {
        updated[def.id] = { unlocked: true, date: new Date().toISOString() };
        changed = true;
        toast.success(`Achievement unlocked: ${def.label}`, { icon: '🏆' });
      }
    });
    if (changed) {
      setAchievements(updated);
      localStorage.setItem(ACHIEVEMENTS_KEY, JSON.stringify(updated));
    }
  }, [achievements]);

  // ── Content Analysis ──
  const analyzeContent = useCallback((text) => {
    const words = text.toLowerCase().split(/\s+/).filter(w => w.length > 0);
    const freq = {};
    words.forEach(w => { const c = w.replace(/[^a-z]/g, ''); if (c.length > 3 && !IGNORE_WORDS.has(c)) freq[c] = (freq[c] || 0) + 1; });
    const ow = Object.entries(freq).filter(([, v]) => v >= 4).sort((a, b) => b[1] - a[1]).slice(0, 7).map(([word, count]) => ({ word, count }));
    setOverusedWords(ow);
    const pm = text.match(PASSIVE_REGEX) || [];
    setPassiveCount(pm.length);
    const wm = WEAK_WORDS.filter(w => text.toLowerCase().includes(w));
    setWeakWordMatches(wm.slice(0, 8));
  }, []);

  // ── Content Change Handler ──
  const handleContentChange = (newContent) => {
    setContent(newContent);
    const wc = newContent.trim() ? newContent.trim().split(/\s+/).length : 0;
    setWordCount(wc);
    setCharCount(newContent.length);
    lastTypingRef.current = Date.now();
    if (!continuousWritingStart) setContinuousWritingStart(Date.now());

    // Auto-save
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      const data = { content: newContent, title, lastSaved: new Date().toISOString() };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      setLastSaved(new Date());
    }, 2000);

    // Dopamine toasts
    MILESTONE_THRESHOLDS.forEach(threshold => {
      if (wc >= threshold && !shownToasts.has(threshold)) {
        setShownToasts(prev => new Set([...prev, threshold]));
        setSessionMilestones(prev => new Set([...prev, threshold]));
        toast(DOPAMINE_MESSAGES[threshold], {
          icon: threshold >= 250 ? '🔥' : '✨',
          duration: 2500,
          className: 'bg-slate-900 text-white border-cyan-500/30',
        });
      }
    });

    // Check achievements
    checkAchievements({
      totalWords: wc,
      sessionSeconds,
      streak,
      dailyWords: dailyWordsUsed + wc,
    });

    // Wow Factor
    if (typingPauseTimer.current) clearTimeout(typingPauseTimer.current);
    typingPauseTimer.current = setTimeout(() => {
      if (!wowVisibleRef.current && newContent.length > 100) {
        const sentences = newContent.split(/[.!?]+/).filter(s => s.trim().length > 5);
        const ws = newContent.split(/\s+/);
        const avgLen = sentences.length > 0 ? Math.round(ws.length / sentences.length) : 0;
        const freq2 = {};
        ws.forEach(w => { const c = w.toLowerCase().replace(/[^a-z]/g, ''); if (c.length > 3) freq2[c] = (freq2[c] || 0) + 1; });
        const over2 = Object.entries(freq2).filter(([, v]) => v >= 6).sort((a, b) => b[1] - a[1]);
        const pool = [];
        if (over2.length) pool.push(`"${over2[0][0]}" appears ${over2[0][1]} times — consider a synonym.`);
        if (avgLen > 22) pool.push("Long, flowing prose — great for immersive scenes.");
        if (avgLen < 10 && sentences.length > 4) pool.push("Short, punchy sentences. Great for pace!");
        pool.push("Keep going — great things take time.", "Nice flow here!", "You're building something real.", "Every word counts.");
        wowVisibleRef.current = true;
        setWowMessage(pool[Math.floor(Math.random() * pool.length)]);
        setTimeout(() => { setWowMessage(null); wowVisibleRef.current = false; }, 4000);
      }
    }, 1800);

    // Analysis (debounced)
    if (newContent.length > 50) analyzeContent(newContent);
    else { setOverusedWords([]); setPassiveCount(0); setWeakWordMatches([]); }
  };

  // ── Search ──
  const handleSearch = (q) => {
    setSearchQuery(q);
    if (!q.trim()) { setSearchMatches([]); return; }
    const lower = content.toLowerCase();
    const ql = q.toLowerCase();
    const matches = [];
    let idx = 0;
    while ((idx = lower.indexOf(ql, idx)) !== -1) {
      const s = Math.max(0, idx - 40);
      const e = Math.min(content.length, idx + q.length + 40);
      matches.push({ pos: idx, snippet: '…' + content.slice(s, e).replace(/\n/g, ' ') + '…' });
      idx += ql.length;
    }
    setSearchMatches(matches);
    setSearchIdx(0);
  };

  // ── Save / Export / Import ──
  const handleSave = () => {
    const data = { content, title, lastSaved: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    setLastSaved(new Date());
    const today = new Date().toDateString();
    localStorage.setItem(DAILY_USAGE_KEY, JSON.stringify({ date: today, words: wordCount }));
    toast.success('Saved!');
  };

  const handleExport = (format) => {
    if (!canExport) { setUpgradeFeature('export'); setShowUpgradePrompt(true); return; }
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${title}.${format}`; a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = (e) => {
    if (!canImport) { setUpgradeFeature('import'); setShowUpgradePrompt(true); return; }
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => { handleContentChange(ev.target.result); setTitle(file.name.replace(/\.[^/.]+$/, '')); };
    reader.readAsText(file);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(content).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const handleClear = () => {
    if (!content) return;
    if (window.confirm('Clear all content? This cannot be undone.')) {
      handleContentChange('');
      setTitle('Untitled');
    }
  };

  const insertStarter = (prompt) => {
    handleContentChange(prompt);
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(prompt.length, prompt.length);
      }
    }, 50);
  };

  // ── Computed values ──
  const usagePercent = Math.min(100, ((dailyWordsUsed + wordCount) / FREE_WORD_LIMIT) * 100);
  const sentences = content.split(/[.!?]+/).filter(s => s.trim().length > 5);
  const paragraphs = content.split(/\n\n+/).filter(p => p.trim().length > 0);
  const dialogueCount = (content.match(/[""\u201C\u201D][^""\u201C\u201D]+[""\u201C\u201D]/g) || []).length;
  const dialoguePct = wordCount > 0 ? Math.round((dialogueCount * 5 / wordCount) * 100) : 0;
  const avgSentLen = sentences.length > 0 ? Math.round(wordCount / sentences.length) : 0;
  const gradeLevel = content.length > 50 ? fleschKincaid(content) : null;
  const todayMission = DAILY_MISSIONS[getDayIndex() % DAILY_MISSIONS.length];
  const todayHint = DAILY_HINTS[getDayIndex() % DAILY_HINTS.length];
  const isKindleBg = isKindle;

  // ── Render ──
  return (
    <div className={`min-h-screen transition-colors duration-300 ${isKindleBg ? 'bg-[#F4ECD8]' : 'bg-[#080E1A]'}`}>
      {/* ═══ HEADER ═══ */}
      <header className={`sticky top-0 z-40 border-b backdrop-blur-md ${isKindleBg ? 'bg-[#F4ECD8]/95 border-amber-200' : 'bg-[#080E1A]/98 border-slate-700/50'}`}>
        <div className="container mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/freebum" className={`flex items-center gap-2 transition-colors ${isKindleBg ? 'text-amber-700 hover:text-amber-900' : 'text-slate-400 hover:text-white'}`}>
            <ChevronLeft className="w-4 h-4" />
            <span className="hidden sm:inline text-sm">Dashboard</span>
          </Link>
          <div className={`w-px h-5 ${isKindleBg ? 'bg-amber-300' : 'bg-slate-700'}`} />
          <input
            type="text" value={title} onChange={(e) => setTitle(e.target.value)}
            className={`flex-1 font-semibold text-base bg-transparent border-none outline-none ${isKindleBg ? 'text-slate-800 placeholder:text-amber-400' : 'text-white placeholder:text-slate-600'}`}
            placeholder="Document Title..."
            data-testid="scratchpad-title"
          />
          <Badge className="hidden sm:flex bg-emerald-500/15 text-emerald-400 border-emerald-500/25 text-[10px] uppercase tracking-wider">Free</Badge>
          {streak > 0 && (
            <div className={`hidden sm:flex items-center gap-1 text-xs px-2 py-1 rounded-full ${isKindleBg ? 'bg-amber-200 text-amber-800' : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'}`} data-testid="scratchpad-streak">
              <Flame className="w-3 h-3" />
              <span>{streak}d</span>
            </div>
          )}
          <Button variant="ghost" size="sm" onClick={handleSave} className={isKindleBg ? 'text-amber-800 hover:bg-amber-200' : 'text-slate-300 hover:text-white'} data-testid="scratchpad-save-btn">
            <Save className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline text-sm">Save</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={handleCopy} className={isKindleBg ? 'text-amber-800 hover:bg-amber-200' : 'text-slate-300 hover:text-white'}>
            {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleExport('txt')} className={isKindleBg ? 'text-amber-800 hover:bg-amber-200' : 'text-slate-300 hover:text-white'}>
            <Download className="w-4 h-4" />
          </Button>
          <input ref={fileInputRef} type="file" accept=".txt,.md" className="hidden" onChange={handleImport} />
          <Button variant="ghost" size="sm" onClick={() => fileInputRef.current?.click()} className={isKindleBg ? 'text-amber-800 hover:bg-amber-200' : 'text-slate-300 hover:text-white'}>
            <Upload className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-5 max-w-7xl">
        {/* ═══ STATUS STRIP ═══ */}
        <div className={`flex items-center justify-between mb-4 text-xs ${isKindleBg ? 'text-amber-700' : 'text-slate-500'}`}>
          <div className="flex items-center gap-3">
            <span className={`font-medium ${isKindleBg ? 'text-amber-800' : 'text-emerald-400'}`} data-testid="word-count-display">{wordCount.toLocaleString()} words</span>
            <span>{charCount.toLocaleString()} chars</span>
            <span>{fmtTime(sessionSeconds)} session</span>
            {lastSaved && <span className={isKindleBg ? 'text-amber-600' : 'text-slate-600'}>Saved {lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>}
          </div>
          <div className="flex items-center gap-2">
            <span>{(dailyWordsUsed + wordCount).toLocaleString()} / {FREE_WORD_LIMIT.toLocaleString()} daily</span>
            <div className={`w-16 h-1.5 rounded-full overflow-hidden ${isKindleBg ? 'bg-amber-200' : 'bg-slate-800'}`}>
              <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${usagePercent}%` }} />
            </div>
          </div>
        </div>

        {/* ═══ MAIN GRID ═══ */}
        <div className="flex gap-5">
          {/* ── LEFT: WRITING CANVAS ── */}
          <div className={`flex-1 min-w-0 transition-all duration-300 ${isFocus ? 'max-w-3xl mx-auto' : ''}`}>

            {/* Daily Writing Mission */}
            {!isFocus && (
              <div className={`mb-4 flex items-start gap-3 p-3.5 rounded-xl border transition-all ${isKindleBg ? 'bg-amber-50 border-amber-200' : 'bg-[#0D1525]/80 border-cyan-500/15'}`} data-testid="daily-mission-banner">
                <div className={`w-1 h-10 rounded-full shrink-0 mt-0.5 ${isKindleBg ? 'bg-amber-400' : 'bg-cyan-400'}`} />
                <div>
                  <p className={`text-[10px] font-semibold uppercase tracking-widest mb-0.5 ${isKindleBg ? 'text-cyan-700' : 'text-cyan-400'}`}>Today's Writing Mission</p>
                  <p className={`text-sm ${isKindleBg ? 'text-slate-700' : 'text-slate-300'}`}>{todayMission}</p>
                </div>
              </div>
            )}

            {/* Writing Mode Switcher */}
            <div className={`flex items-center gap-2 mb-4`}>
              <div className={`flex rounded-lg overflow-hidden border ${isKindleBg ? 'border-amber-300 bg-amber-50' : 'border-slate-700/60 bg-slate-900/40'}`}>
                {[
                  { id: 'freewrite', label: 'Free Write', icon: Pen },
                  { id: 'goal', label: 'Goal Mode', icon: Target },
                  { id: 'focus', label: 'Focus Mode', icon: Moon },
                ].map(mode => (
                  <button key={mode.id}
                    onClick={() => { setWritingMode(mode.id); if (mode.id !== 'focus') setKindleMode(false); }}
                    className={`flex items-center gap-1.5 px-3.5 py-2 text-xs font-medium transition-all ${
                      writingMode === mode.id
                        ? mode.id === 'focus' ? (isKindleBg ? 'bg-amber-300 text-amber-900' : 'bg-amber-500/20 text-amber-300')
                          : mode.id === 'goal' ? (isKindleBg ? 'bg-emerald-200 text-emerald-800' : 'bg-emerald-500/15 text-emerald-300')
                          : (isKindleBg ? 'bg-cyan-200 text-cyan-800' : 'bg-cyan-500/15 text-cyan-300')
                        : isKindleBg ? 'text-slate-500 hover:text-slate-700 hover:bg-amber-100' : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/40'
                    }`}
                    data-testid={`mode-${mode.id}`}
                  >
                    <mode.icon className="w-3.5 h-3.5" />
                    {mode.label}
                  </button>
                ))}
              </div>
              {/* Focus: Kindle sub-toggle */}
              {isFocus && (
                <button onClick={() => setKindleMode(k => !k)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-all ${kindleMode ? 'bg-amber-200 border-amber-300 text-amber-800' : 'bg-slate-800/40 border-slate-700/50 text-slate-400 hover:border-amber-400 hover:text-amber-300'}`}
                  data-testid="scratchpad-kindle-toggle"
                >
                  {kindleMode ? <Sun className="w-3.5 h-3.5" /> : <Type className="w-3.5 h-3.5" />}
                  Kindle {kindleMode ? 'On' : 'Off'}
                </button>
              )}
              {/* Goal mode: milestone mini-progress */}
              {isGoal && (
                <div className="flex items-center gap-2 ml-auto">
                  {MILESTONE_THRESHOLDS.map(m => (
                    <div key={m} className={`flex items-center gap-1 text-[10px] font-medium ${sessionMilestones.has(m) ? 'text-emerald-400' : isKindleBg ? 'text-slate-400' : 'text-slate-600'}`}>
                      {sessionMilestones.has(m) ? <Check className="w-3 h-3" /> : <span className="w-3 h-3 rounded-full border border-current inline-block" />}
                      {m}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Flow Status Indicator */}
            <AnimatePresence>
              {flowStatus && isFocus && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                  className={`mb-3 text-center py-1.5 rounded-lg text-xs font-medium ${
                    flowStatus === 'locked' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' :
                    flowStatus === 'flow' ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20' :
                    'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                  }`}
                  data-testid="flow-status"
                >
                  {flowStatus === 'warming' && "You're warming up."}
                  {flowStatus === 'flow' && "You're in flow."}
                  {flowStatus === 'locked' && "Locked in."}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Editor Box */}
            {content.length === 0 && !startedWriting ? (
              /* ── Empty State ── */
              <div className={`w-full min-h-[65vh] rounded-xl p-6 sm:p-8 flex flex-col items-center justify-center border transition-all ${isKindleBg ? 'bg-[#F4ECD8] border-amber-200' : 'bg-[#0D1321] border-slate-700/50'}`} data-testid="empty-state">
                <h2 className={`text-2xl font-literary font-semibold mb-2 ${isKindleBg ? 'text-slate-800' : 'text-white'}`}>What happens next?</h2>
                <p className={`text-sm mb-5 ${isKindleBg ? 'text-amber-700' : 'text-slate-400'}`}>Pick a first line, or start from scratch.</p>
                <div className="flex flex-wrap justify-center gap-2 max-w-2xl mb-5">
                  {STARTER_PROMPTS.map((prompt, i) => (
                    <button key={i} onClick={() => insertStarter(prompt)}
                      className={`px-4 py-2 rounded-full text-sm border transition-all hover:scale-[1.02] text-left ${isKindleBg
                        ? 'border-amber-300 text-amber-800 hover:bg-amber-200 hover:border-amber-400'
                        : 'border-cyan-500/25 text-cyan-300 hover:border-cyan-400/50 hover:bg-cyan-950/30 hover:shadow-[0_0_12px_rgba(34,211,238,0.1)]'
                      }`}
                      data-testid={`starter-prompt-${i}`}
                    >
                      "{prompt}"
                    </button>
                  ))}
                </div>
                {/* Just Start Writing button */}
                <Button
                  onClick={() => {
                    setStartedWriting(true);
                    setTimeout(() => {
                      if (textareaRef.current) textareaRef.current.focus();
                    }, 100);
                  }}
                  variant="gold"
                  className="px-8 py-3 text-sm font-semibold"
                  data-testid="just-start-writing-btn"
                >
                  <PenTool className="w-4 h-4 mr-2" />
                  Just Start Writing
                </Button>
                {/* Hidden textarea for typing to work */}
                <textarea
                  ref={textareaRef}
                  value={content}
                  onChange={(e) => handleContentChange(e.target.value)}
                  className="absolute opacity-0 pointer-events-none w-0 h-0"
                  data-testid="scratchpad-textarea-hidden"
                />
              </div>
            ) : (
              /* ── Active Editor ── */
              <textarea
                ref={textareaRef}
                value={content}
                onChange={(e) => handleContentChange(e.target.value)}
                spellCheck={true}
                autoCorrect="on"
                autoCapitalize="sentences"
                placeholder="Start writing..."
                className={`w-full min-h-[65vh] rounded-xl p-6 resize-none focus:outline-none transition-all duration-300 text-lg leading-relaxed font-literary ${
                  isKindleBg
                    ? 'kindle-mode-textarea border border-amber-200 placeholder:text-amber-400'
                    : 'bg-[#0D1321] border border-slate-700/50 text-slate-200 placeholder:text-slate-600 focus:border-cyan-500/30 focus:shadow-[0_0_20px_rgba(34,211,238,0.05)]'
                }`}
                style={{ lineHeight: '1.85' }}
                data-testid="scratchpad-textarea"
              />
            )}

            {/* Wow Factor */}
            <AnimatePresence>
              {wowMessage && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                  className={`mt-2 px-4 py-2 rounded-lg text-sm flex items-center gap-2 ${isKindleBg ? 'bg-amber-100 border border-amber-300 text-amber-800' : 'bg-cyan-950/60 border border-cyan-500/25 text-cyan-300'}`}
                  data-testid="scratchpad-wow-message"
                >
                  <Sparkles className="w-4 h-4 shrink-0" />
                  {wowMessage}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Overused words strip */}
            {overusedWords.length > 0 && !isFocus && (
              <div className={`mt-2 flex items-center gap-2 flex-wrap p-2 rounded-lg ${isKindleBg ? 'bg-amber-100' : 'bg-slate-900/50'}`}>
                <span className={`text-[10px] font-semibold uppercase tracking-wider shrink-0 ${isKindleBg ? 'text-amber-700' : 'text-rose-400'}`}>Overused:</span>
                {overusedWords.slice(0, 5).map(({ word, count }) => (
                  <button key={word} onClick={() => { handleSearch(word); setRightTab('enhance'); }}
                    className={`text-[11px] px-2 py-0.5 rounded-full transition-all hover:scale-105 ${isKindleBg ? 'bg-amber-200 text-amber-800 hover:bg-amber-300' : 'bg-rose-900/40 text-rose-300 border border-rose-500/30 hover:bg-rose-900/60'}`}
                    data-testid={`overused-${word}`}
                  >
                    {word} x{count}
                  </button>
                ))}
              </div>
            )}

            {/* Bottom CTA */}
            {!isFocus && (
              <div className="mt-5 space-y-3">
                {/* Free Features link */}
                <Link to="/freebum" className="block" data-testid="free-features-link">
                  <div className={`p-4 rounded-xl border transition-all hover:scale-[1.005] ${isKindleBg ? 'bg-amber-50 border-amber-200 hover:border-amber-300' : 'bg-gradient-to-r from-violet-950/40 via-slate-900/50 to-cyan-950/40 border-violet-500/20 hover:border-violet-500/30'}`}>
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center border ${isKindleBg ? 'bg-amber-200 border-amber-300' : 'bg-gradient-to-br from-violet-500/20 to-cyan-500/20 border-violet-500/30'}`}>
                          <Sparkles className={`w-4 h-4 ${isKindleBg ? 'text-amber-700' : 'text-violet-400'}`} />
                        </div>
                        <div>
                          <p className={`font-medium text-sm ${isKindleBg ? 'text-slate-800' : 'text-white'}`}>Try Shweebum Spark — Free Writing Tools</p>
                          <p className={`text-xs ${isKindleBg ? 'text-slate-600' : 'text-slate-400'}`}>Story Builder, Character Creator, Writing Polish & more — free samples included</p>
                        </div>
                      </div>
                      <Button size="sm" className="bg-gradient-to-r from-violet-500 to-cyan-500 hover:from-violet-400 hover:to-cyan-400 text-white text-sm whitespace-nowrap shadow-lg shadow-violet-500/20">
                        <Sparkles className="w-3.5 h-3.5 mr-1.5" />Explore Tools
                      </Button>
                    </div>
                  </div>
                </Link>
                {/* Turn into a book CTA */}
                <div className={`p-4 rounded-xl border ${isKindleBg ? 'bg-amber-50 border-amber-200' : 'bg-gradient-to-r from-cyan-950/30 via-slate-900/50 to-violet-950/30 border-cyan-500/20'}`} data-testid="upgrade-cta">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center border ${isKindleBg ? 'bg-amber-200 border-amber-300' : 'bg-gradient-to-br from-cyan-500/20 to-violet-500/20 border-cyan-500/30'}`}>
                        <BookOpen className={`w-4 h-4 ${isKindleBg ? 'text-amber-700' : 'text-cyan-400'}`} />
                      </div>
                      <div>
                        <p className={`font-medium text-sm ${isKindleBg ? 'text-slate-800' : 'text-white'}`}>Ready to turn this into a real book?</p>
                        <p className={`text-xs ${isKindleBg ? 'text-slate-600' : 'text-slate-400'}`}>Take what you've started and build it into chapters.</p>
                      </div>
                    </div>
                    <Link to="/freebum">
                      <Button size="sm" variant="gold" className="text-sm whitespace-nowrap" data-testid="start-book-cta">
                        <BookOpen className="w-3.5 h-3.5 mr-1.5" />Turn This Into a Book
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── RIGHT SIDEBAR ── */}
          {!isFocus && (
            <div className="w-72 shrink-0 hidden lg:flex flex-col gap-3">
              {/* Tab Switcher */}
              <div className={`flex rounded-xl overflow-hidden border ${isKindleBg ? 'border-amber-200 bg-amber-50' : 'border-slate-700/60 bg-slate-900/50'}`}>
                {[
                  { id: 'progress', label: 'Progress', color: 'emerald' },
                  { id: 'improve', label: 'Improve', color: 'cyan' },
                  { id: 'enhance', label: 'Enhance', color: 'violet' },
                ].map(tab => (
                  <button key={tab.id} onClick={() => setRightTab(tab.id)}
                    className={`flex-1 py-2.5 text-[11px] font-semibold uppercase tracking-wider transition-all ${
                      rightTab === tab.id
                        ? tab.color === 'emerald' ? (isKindleBg ? 'bg-emerald-100 text-emerald-700' : 'bg-emerald-500/20 text-emerald-300 border-b-2 border-emerald-400')
                          : tab.color === 'cyan' ? (isKindleBg ? 'bg-cyan-100 text-cyan-700' : 'bg-cyan-500/20 text-cyan-300 border-b-2 border-cyan-400')
                          : (isKindleBg ? 'bg-violet-100 text-violet-700' : 'bg-violet-500/20 text-violet-300 border-b-2 border-violet-400')
                        : isKindleBg ? 'text-slate-500 hover:text-slate-700 hover:bg-amber-100' : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/40'
                    }`}
                    data-testid={`scratchpad-tab-${tab.id}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* ═══ PROGRESS TAB ═══ */}
              {rightTab === 'progress' && (
                <div className="space-y-3">
                  {/* Stats grid */}
                  <Card className={isKindleBg ? 'bg-amber-50 border-amber-200' : 'bg-[#0D1525]/80 border-slate-700/50'}>
                    <CardContent className="p-3 space-y-3">
                      <p className={`text-[10px] font-semibold uppercase tracking-widest ${isKindleBg ? 'text-slate-500' : 'text-slate-400'}`}>Writing Stats</p>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { label: 'Words', value: wordCount.toLocaleString(), color: 'emerald' },
                          { label: 'Sentences', value: sentences.length, color: 'cyan' },
                          { label: 'Paragraphs', value: paragraphs.length, color: 'violet' },
                          { label: 'Read time', value: `${Math.max(1, Math.round(wordCount / 238))} min`, color: 'amber' },
                        ].map(s => (
                          <div key={s.label} className={`rounded-lg p-2 ${isKindleBg ? 'bg-amber-100' : 'bg-slate-900/60'}`}>
                            <p className="text-[9px] text-slate-500 uppercase">{s.label}</p>
                            <p className={`text-lg font-bold ${isKindleBg ? 'text-slate-800' : 'text-white'}`}>{s.value}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Session & Streak */}
                  <Card className={isKindleBg ? 'bg-amber-50 border-amber-200' : 'bg-[#0D1525]/80 border-amber-500/20'}>
                    <CardContent className="p-3 space-y-2">
                      <p className={`text-[10px] font-semibold uppercase tracking-widest ${isKindleBg ? 'text-amber-700' : 'text-amber-400'}`}>Session & Streak</p>
                      <div className={`flex items-center justify-between px-3 py-2 rounded-lg ${isKindleBg ? 'bg-amber-100' : 'bg-slate-900/60'}`}>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-amber-400" />
                          <span className={`text-sm font-medium ${isKindleBg ? 'text-slate-700' : 'text-white'}`}>{fmtTime(sessionSeconds)}</span>
                        </div>
                        <span className="text-xs text-slate-500">this session</span>
                      </div>
                      {streak > 0 && (
                        <div className={`flex items-center justify-between px-3 py-2 rounded-lg ${isKindleBg ? 'bg-amber-100' : 'bg-amber-950/40 border border-amber-500/20'}`} data-testid="scratchpad-streak-panel">
                          <div className="flex items-center gap-2">
                            <Flame className="w-4 h-4 text-amber-400" />
                            <span className={`text-sm font-bold ${isKindleBg ? 'text-amber-800' : 'text-amber-300'}`}>{streak} day streak!</span>
                          </div>
                          {streak >= 3 && <Star className="w-4 h-4 text-amber-400" />}
                        </div>
                      )}
                      {/* Daily goal */}
                      <div>
                        <div className="flex justify-between text-xs mb-1">
                          <span className={isKindleBg ? 'text-slate-500' : 'text-slate-400'}>Daily limit</span>
                          <span className={isKindleBg ? 'text-slate-600' : 'text-slate-400'}>{(dailyWordsUsed + wordCount).toLocaleString()} / {FREE_WORD_LIMIT.toLocaleString()}</span>
                        </div>
                        <div className={`w-full h-1.5 rounded-full overflow-hidden ${isKindleBg ? 'bg-amber-200' : 'bg-slate-800'}`}>
                          <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${usagePercent}%` }} />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Milestones */}
                  <Card className={isKindleBg ? 'bg-amber-50 border-amber-200' : 'bg-[#0D1525]/80 border-emerald-500/15'}>
                    <CardContent className="p-3">
                      <p className={`text-[10px] font-semibold uppercase tracking-widest mb-2 ${isKindleBg ? 'text-emerald-700' : 'text-emerald-400'}`}>Session Milestones</p>
                      <div className="space-y-1.5">
                        {MILESTONE_THRESHOLDS.map(m => {
                          const done = wordCount >= m;
                          return (
                            <div key={m} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg transition-all ${done
                              ? (isKindleBg ? 'bg-emerald-100 text-emerald-700' : 'bg-emerald-500/10 text-emerald-400')
                              : (isKindleBg ? 'text-slate-400' : 'text-slate-600')
                            }`} data-testid={`milestone-${m}`}>
                              {done ? <Check className="w-3.5 h-3.5" /> : <span className="w-3.5 h-3.5 rounded-full border border-current" />}
                              <span className="text-xs font-medium">{m} words</span>
                              {done && <span className={`ml-auto text-[10px] ${isKindleBg ? 'text-emerald-600' : 'text-emerald-500'}`}>Done!</span>}
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* ═══ IMPROVE TAB ═══ */}
              {rightTab === 'improve' && (
                <div className="space-y-3">
                  {content.length < 50 ? (
                    <div className={`text-center py-10 ${isKindleBg ? 'text-amber-600' : 'text-slate-500'}`}>
                      <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">Write a few sentences to see analysis.</p>
                    </div>
                  ) : (
                    <>
                      {/* Prose Stats */}
                      <Card className={isKindleBg ? 'bg-amber-50 border-amber-200' : 'bg-[#0D1525]/80 border-cyan-500/20'}>
                        <CardContent className="p-3 space-y-2">
                          <p className={`text-[10px] font-semibold uppercase tracking-widest ${isKindleBg ? 'text-cyan-700' : 'text-cyan-400'}`}>Prose Analysis</p>
                          {[
                            { label: 'Avg sentence', value: `${avgSentLen} words`, note: avgSentLen < 12 ? 'Short & punchy' : avgSentLen < 20 ? 'Balanced' : 'Long & flowing' },
                            { label: 'Dialogue', value: `${Math.min(100, dialoguePct)}%`, note: dialoguePct > 25 ? 'Dialogue-heavy' : 'Mostly narrative' },
                            { label: 'Readability', value: gradeLevel ? `Grade ${gradeLevel}` : '–', note: gradeLevel ? (gradeLevel <= 6 ? 'Easy read' : gradeLevel <= 10 ? 'Average' : 'Complex') : '' },
                          ].map(s => (
                            <div key={s.label} className={`flex items-center justify-between py-1.5 border-b last:border-0 ${isKindleBg ? 'border-amber-200' : 'border-slate-700/30'}`}>
                              <span className={`text-xs ${isKindleBg ? 'text-slate-600' : 'text-slate-400'}`}>{s.label}</span>
                              <div className="text-right">
                                <span className={`text-sm font-semibold ${isKindleBg ? 'text-slate-800' : 'text-white'}`}>{s.value}</span>
                                {s.note && <p className="text-[10px] text-slate-500">{s.note}</p>}
                              </div>
                            </div>
                          ))}
                        </CardContent>
                      </Card>

                      {/* Issues */}
                      <Card className={isKindleBg ? 'bg-amber-50 border-amber-200' : 'bg-[#0D1525]/80 border-rose-500/20'}>
                        <CardContent className="p-3 space-y-2">
                          <p className={`text-[10px] font-semibold uppercase tracking-widest ${isKindleBg ? 'text-rose-700' : 'text-rose-400'}`}>Writing Issues</p>
                          <div className={`flex items-center justify-between py-1.5 border-b ${isKindleBg ? 'border-amber-200' : 'border-slate-700/30'}`}>
                            <span className={`text-xs flex items-center gap-1.5 ${isKindleBg ? 'text-slate-600' : 'text-slate-400'}`}>
                              <Eye className="w-3.5 h-3.5 text-rose-400" />Passive voice
                            </span>
                            <span className={`text-sm font-semibold ${passiveCount > 5 ? 'text-rose-400' : isKindleBg ? 'text-slate-800' : 'text-white'}`}>{passiveCount}</span>
                          </div>
                          <div className={`flex items-center justify-between py-1.5 border-b ${isKindleBg ? 'border-amber-200' : 'border-slate-700/30'}`}>
                            <span className={`text-xs flex items-center gap-1.5 ${isKindleBg ? 'text-slate-600' : 'text-slate-400'}`}>
                              <Zap className="w-3.5 h-3.5 text-amber-400" />Weak words
                            </span>
                            <span className={`text-sm font-semibold ${weakWordMatches.length > 4 ? 'text-amber-400' : isKindleBg ? 'text-slate-800' : 'text-white'}`}>{weakWordMatches.length}</span>
                          </div>
                          {weakWordMatches.length > 0 && (
                            <div className="flex flex-wrap gap-1 pt-1">
                              {weakWordMatches.map(w => (
                                <button key={w} onClick={() => { handleSearch(w); setRightTab('enhance'); }}
                                  className={`text-[10px] px-1.5 py-0.5 rounded ${isKindleBg ? 'bg-amber-200 text-amber-800' : 'bg-amber-900/30 text-amber-300 border border-amber-500/20'}`}
                                  data-testid={`weak-word-${w}`}
                                >
                                  {w}
                                </button>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Overused words */}
                      {overusedWords.length > 0 && (
                        <Card className={isKindleBg ? 'bg-amber-50 border-amber-200' : 'bg-[#0D1525]/80 border-rose-500/15'}>
                          <CardContent className="p-3">
                            <p className={`text-[10px] font-semibold uppercase tracking-widest mb-2 ${isKindleBg ? 'text-rose-700' : 'text-rose-400'}`}>Overused Words</p>
                            <div className="space-y-1">
                              {overusedWords.slice(0, 6).map(({ word, count }) => (
                                <div key={word} className={`flex items-center justify-between py-1 ${isKindleBg ? 'border-b border-amber-100' : 'border-b border-slate-700/20'} last:border-0`}>
                                  <button onClick={() => { handleSearch(word); setRightTab('enhance'); }}
                                    className={`text-xs hover:underline ${isKindleBg ? 'text-slate-700' : 'text-slate-300'}`} data-testid={`analyze-overused-${word}`}>
                                    {word}
                                  </button>
                                  <span className={`text-xs font-medium ${isKindleBg ? 'text-rose-600' : 'text-rose-400'}`}>x{count}</span>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </>
                  )}

                  {/* Daily Hint */}
                  <Card className={isKindleBg ? 'bg-amber-50 border-amber-200' : 'bg-[#0D1525]/80 border-violet-500/15'}>
                    <CardContent className="p-3">
                      <p className={`text-[10px] font-semibold uppercase tracking-widest mb-1.5 ${isKindleBg ? 'text-violet-700' : 'text-violet-400'}`}>Daily Writing Tip</p>
                      <p className={`text-xs leading-relaxed ${isKindleBg ? 'text-slate-700' : 'text-slate-300'}`}>
                        <Lightbulb className={`w-3.5 h-3.5 inline mr-1.5 -mt-0.5 ${isKindleBg ? 'text-amber-500' : 'text-amber-400'}`} />
                        {todayHint}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* ═══ ENHANCE TAB ═══ */}
              {rightTab === 'enhance' && (
                <div className="space-y-3">
                  {/* Smart Search */}
                  <Card className={isKindleBg ? 'bg-amber-50 border-amber-200' : 'bg-[#0D1525]/80 border-violet-500/20'}>
                    <CardContent className="p-3">
                      <p className={`text-[10px] font-semibold uppercase tracking-widest mb-2 ${isKindleBg ? 'text-violet-700' : 'text-violet-400'}`}>Smart Search</p>
                      <div className={`flex items-center gap-2 rounded-lg px-3 py-2 mb-2 ${isKindleBg ? 'bg-amber-100 border border-amber-200' : 'bg-slate-900/60 border border-slate-700/40'}`}>
                        <Search className="w-3.5 h-3.5 shrink-0 text-slate-500" />
                        <input type="text" value={searchQuery} onChange={(e) => handleSearch(e.target.value)}
                          placeholder="Search your writing..."
                          className={`flex-1 text-xs bg-transparent outline-none ${isKindleBg ? 'text-slate-800 placeholder:text-amber-400' : 'text-white placeholder:text-slate-500'}`}
                          data-testid="scratchpad-search-input"
                        />
                        {searchQuery && (
                          <div className="flex items-center gap-0.5">
                            <span className={`text-[10px] ${isKindleBg ? 'text-slate-500' : 'text-cyan-400'}`}>{searchMatches.length > 0 ? `${searchIdx + 1}/${searchMatches.length}` : '0'}</span>
                            <button onClick={() => setSearchIdx(i => Math.max(0, i - 1))} className="p-0.5 text-slate-500 hover:text-white"><ChevronUp className="w-3 h-3" /></button>
                            <button onClick={() => setSearchIdx(i => Math.min(searchMatches.length - 1, i + 1))} className="p-0.5 text-slate-500 hover:text-white"><ChevronDown className="w-3 h-3" /></button>
                          </div>
                        )}
                      </div>
                      {searchMatches.length > 0 ? (
                        <div className="space-y-1 max-h-48 overflow-y-auto">
                          {searchMatches.slice(0, 8).map((m, idx) => (
                            <div key={idx} onClick={() => setSearchIdx(idx)}
                              className={`text-[11px] p-2 rounded cursor-pointer ${idx === searchIdx ? (isKindleBg ? 'bg-amber-200' : 'bg-violet-900/30 border border-violet-500/30') : (isKindleBg ? 'hover:bg-amber-100' : 'hover:bg-slate-800/40')}`}
                              dangerouslySetInnerHTML={{ __html: m.snippet.replace(new RegExp(searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), s => `<mark style="background:rgba(245,158,11,0.3);color:${isKindleBg ? '#92400e' : '#fcd34d'};border-radius:2px;padding:0 2px">${s}</mark>`) }}
                            />
                          ))}
                        </div>
                      ) : searchQuery ? (
                        <p className="text-xs text-center py-3 text-slate-500">No matches for "{searchQuery}"</p>
                      ) : (
                        <p className={`text-xs text-center py-3 ${isKindleBg ? 'text-slate-400' : 'text-slate-600'}`}>Type to search your writing</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Writing Environment */}
                  <Card className={isKindleBg ? 'bg-amber-50 border-amber-200' : 'bg-[#0D1525]/80 border-amber-500/20'}>
                    <CardContent className="p-3 space-y-2">
                      <p className={`text-[10px] font-semibold uppercase tracking-widest ${isKindleBg ? 'text-amber-700' : 'text-amber-400'}`}>Writing Environment</p>
                      <button onClick={() => setWritingMode(m => m === 'focus' ? 'freewrite' : 'focus')}
                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg border transition-all ${isFocus ? (isKindleBg ? 'bg-amber-200 border-amber-300 text-amber-800' : 'bg-amber-900/30 border-amber-500/40 text-amber-300') : (isKindleBg ? 'border-amber-200 text-slate-700 hover:bg-amber-100' : 'border-slate-700/50 text-slate-300 hover:bg-slate-800/40')}`}
                        data-testid="scratchpad-focus-toggle"
                      >
                        <div className="flex items-center gap-2">
                          {isFocus ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                          <span className="text-sm font-medium">{isFocus ? 'Exit Focus Mode' : 'Focus Mode'}</span>
                        </div>
                        {isFocus && <Badge className="text-[9px] bg-amber-500/20 text-amber-400 border-amber-500/30">ON</Badge>}
                      </button>
                      <button onClick={() => setKindleMode(k => !k)}
                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg border transition-all ${kindleMode ? 'bg-amber-200 border-amber-300 text-amber-800' : isKindleBg ? 'border-amber-200 text-slate-700 hover:bg-amber-100' : 'border-slate-700/50 text-slate-300 hover:bg-slate-800/40'}`}
                        data-testid="scratchpad-kindle-panel-toggle"
                      >
                        <div className="flex items-center gap-2">
                          <Type className="w-4 h-4 text-amber-400" />
                          <span className="text-sm font-medium">Kindle Mode</span>
                        </div>
                        {kindleMode && <Badge className="text-[9px] bg-amber-500/20 text-amber-400 border-amber-500/30">ON</Badge>}
                      </button>
                    </CardContent>
                  </Card>

                  {/* Session Reflection */}
                  <Card className={isKindleBg ? 'bg-amber-50 border-amber-200' : 'bg-[#0D1525]/80 border-cyan-500/15'}>
                    <CardContent className="p-3">
                      <p className={`text-[10px] font-semibold uppercase tracking-widest mb-2 ${isKindleBg ? 'text-cyan-700' : 'text-cyan-400'}`}>Session Reflection</p>
                      {sessionReflection ? (
                        <div className={`text-center py-2 rounded-lg text-xs ${isKindleBg ? 'bg-amber-100 text-amber-800' : 'bg-slate-900/60 text-slate-300'}`}>
                          You said this session felt: <strong className={sessionReflection === 'good' ? 'text-emerald-400' : sessionReflection === 'hard' ? 'text-amber-400' : ''}>{sessionReflection}</strong>
                        </div>
                      ) : (
                        <>
                          <p className={`text-xs mb-2 ${isKindleBg ? 'text-slate-600' : 'text-slate-400'}`}>How did that session feel?</p>
                          <div className="flex gap-2">
                            {[
                              { label: 'Good', value: 'good', icon: Heart, color: 'emerald' },
                              { label: 'Okay', value: 'okay', icon: Coffee, color: 'cyan' },
                              { label: 'Hard', value: 'hard', icon: Flame, color: 'amber' },
                            ].map(opt => (
                              <button key={opt.value}
                                onClick={() => { setSessionReflection(opt.value); toast.success(`Noted. Keep going!`); }}
                                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium border transition-all hover:scale-[1.02] ${isKindleBg
                                  ? `border-${opt.color}-300 text-${opt.color}-700 hover:bg-${opt.color}-100`
                                  : `border-${opt.color}-500/25 text-${opt.color}-400 hover:bg-${opt.color}-500/10`
                                }`}
                                data-testid={`reflection-${opt.value}`}
                              >
                                <opt.icon className="w-3.5 h-3.5" />
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>

                  {/* Mini Achievements */}
                  <Card className={isKindleBg ? 'bg-amber-50 border-amber-200' : 'bg-[#0D1525]/80 border-amber-500/15'}>
                    <CardContent className="p-3">
                      <p className={`text-[10px] font-semibold uppercase tracking-widest mb-2 ${isKindleBg ? 'text-amber-700' : 'text-amber-400'}`}>Achievements</p>
                      <div className="space-y-1.5">
                        {ACHIEVEMENT_DEFS.map(def => {
                          const unlocked = !!achievements[def.id];
                          const Icon = def.icon;
                          return (
                            <div key={def.id} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs ${unlocked
                              ? (isKindleBg ? 'bg-amber-100 text-amber-800' : 'bg-amber-500/10 text-amber-300')
                              : (isKindleBg ? 'text-slate-400' : 'text-slate-600')
                            }`} data-testid={`achievement-${def.id}`}>
                              <Icon className={`w-3.5 h-3.5 ${unlocked ? 'text-amber-400' : ''}`} />
                              <span className="font-medium">{def.label}</span>
                              {unlocked && <Award className="w-3.5 h-3.5 ml-auto text-amber-400" />}
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      <UpgradePrompt open={showUpgradePrompt} onClose={() => setShowUpgradePrompt(false)} feature={upgradeFeature} currentTier={userTier} />
    </div>
  );
};

export default FreeScratchpad;
